import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Save, Database, Server, Key, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const SystemSettingsForm = () => {
  const { t } = useTranslation('admin');
  const { authToken } = useAuth();
  const { systemSettings, setSystemSettings, setLoading, setError } = useAdminStore();
  const [formData, setFormData] = useState({
    backup: {
      enabled: true,
      frequency: 'daily',
      retention: 30,
    },
    server: {
      url: '',
      timeout: 30000,
    },
    api: {
      keys: [],
    },
    theme: {
      default: 'system',
    },
  });

  useEffect(() => {
    loadSettings();
  }, [authToken]);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getSystemSettings({ token: authToken });
      if (result.success) {
        setSystemSettings(result.settings);
        setFormData(result.settings);
      } else {
        setError(result.error || t('systemSettingsForm.loadFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('systemSettingsForm.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.updateSystemSettings({
        token: authToken,
        settings: formData,
      });
      if (result.success) {
        setSystemSettings(result.settings);
        alert(t('systemSettingsForm.savedAlert'));
      } else {
        setError(result.error || t('systemSettingsForm.saveFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('systemSettingsForm.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{t('systemSettingsForm.title')}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('systemSettingsForm.subtitle')}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Backup Settings */}
        <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Database className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{t('systemSettingsForm.backup.sectionTitle')}</h4>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('systemSettingsForm.backup.enableLabel')}
              </label>
              <input
                type="checkbox"
                checked={formData.backup.enabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    backup: { ...formData.backup, enabled: e.target.checked },
                  })
                }
                className="w-4 h-4 text-[#3d8ef0] rounded focus:ring-[#3d8ef0]/50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('systemSettingsForm.backup.frequencyLabel')}
              </label>
              <select
                value={formData.backup.frequency}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    backup: { ...formData.backup, frequency: e.target.value },
                  })
                }
                className="w-full px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
              >
                <option value="hourly">{t('systemSettingsForm.backup.frequencies.hourly')}</option>
                <option value="daily">{t('systemSettingsForm.backup.frequencies.daily')}</option>
                <option value="weekly">{t('systemSettingsForm.backup.frequencies.weekly')}</option>
                <option value="monthly">{t('systemSettingsForm.backup.frequencies.monthly')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('systemSettingsForm.backup.retentionLabel')}
              </label>
              <input
                type="number"
                value={formData.backup.retention}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    backup: { ...formData.backup, retention: parseInt(e.target.value) },
                  })
                }
                className="w-full px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                min="1"
              />
            </div>
          </div>
        </div>

        {/* Server Settings */}
        <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Server className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('systemSettingsForm.server.sectionTitle')}
            </h4>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('systemSettingsForm.server.urlLabel')}
              </label>
              <input
                type="url"
                value={formData.server.url}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    server: { ...formData.server, url: e.target.value },
                  })
                }
                className="w-full px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                placeholder="https://api.example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('systemSettingsForm.server.timeoutLabel')}
              </label>
              <input
                type="number"
                value={formData.server.timeout}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    server: { ...formData.server, timeout: parseInt(e.target.value) },
                  })
                }
                className="w-full px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
                min="1000"
              />
            </div>
          </div>
        </div>

        {/* API Keys */}
        <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Key className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{t('systemSettingsForm.apiKeys.sectionTitle')}</h4>
          </div>
          <div className="space-y-2">
            {formData.api.keys.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('systemSettingsForm.apiKeys.noKeysYet')}
              </p>
            ) : (
              formData.api.keys.map((key, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#161f31] rounded-lg"
                >
                  <span className="text-sm font-mono text-gray-900 dark:text-white">
                    {key.substring(0, 20)}...
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      const newKeys = formData.api.keys.filter((_, i) => i !== index);
                      setFormData({
                        ...formData,
                        api: { ...formData.api, keys: newKeys },
                      });
                    }}
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm"
                  >
                    {t('systemSettingsForm.apiKeys.delete')}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Theme Settings */}
        <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Palette className="w-5 h-5 text-[#3d8ef0] dark:text-[#7ab8f8]" />
            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">{t('systemSettingsForm.theme.sectionTitle')}</h4>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('systemSettingsForm.theme.defaultThemeLabel')}
            </label>
            <select
              value={formData.theme.default}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  theme: { ...formData.theme, default: e.target.value },
                })
              }
              className="w-full px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50"
            >
              <option value="system">{t('systemSettingsForm.theme.system')}</option>
              <option value="dark">{t('systemSettingsForm.theme.dark')}</option>
            </select>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <motion.button
            type="submit"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="flex items-center space-x-2 px-6 py-3 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors"
          >
            <Save className="w-5 h-5" />
            <span>{t('systemSettingsForm.saveSettings')}</span>
          </motion.button>
        </div>
      </form>
    </div>
  );
};

export default SystemSettingsForm;


